/* Movatic WMS — Frontend | CesarFilho 932048016 */
import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import './index.css'

import { queryClient } from './lib/queryClient'
import { initTheme } from './lib/theme'
import i18n from './i18n'
import { registerSW } from 'virtual:pwa-register'
import { router } from './routes/router'
import { ToastProvider } from '@/components/Toast'

/* ------------------------------------------------------------------ */
/* 1) DETECÇÃO DE HANDHELD — roda o mais cedo possível no boot        */
/* ------------------------------------------------------------------ */

declare global {
    interface Window {
        __IS_HANDHELD__?: boolean
    }
}

function readOverrideFromURL() {
    const p = new URLSearchParams(window.location.search)
    if (p.has('mobile')) {
        const v = p.get('mobile')
        if (v === '1' || v?.toLowerCase() === 'true') {
            localStorage.setItem('forceMobile', '1')
        } else if (v === '0' || v?.toLowerCase() === 'false') {
            localStorage.setItem('forceMobile', '0')
        }
    }
}

function getForcedMobile(): boolean | undefined {
    const v = localStorage.getItem('forceMobile')
    if (v === '1') return true
    if (v === '0') return false
    return undefined
}

function isHandheldByHeuristics(): boolean {
    const ua = (navigator.userAgent || navigator.vendor || '').toLowerCase()

    // Marcas/modelos comuns de coletores
    const ruggedRE =
        /(zebra|tc2|tc5|tc7|ec3|ec5|dt50|honeywell|scanpal|ck65|cn80|eda5|eda51|eda52|eda7|pm66)/i

    // PWA instalado (standalone) costuma ser uso mobile
    const isStandalone =
        (window.matchMedia &&
            window.matchMedia('(display-mode: standalone)').matches) ||
        // iOS
        // @ts-ignore
        (window.navigator as any).standalone === true

    const narrowTouch =
        (window.screen?.width || 0) <= 520 && navigator.maxTouchPoints > 0

    return ruggedRE.test(ua) || isStandalone || narrowTouch
}

function setupHandheldFlag() {
    // permite override via URL (?mobile=1/0) e persiste
    readOverrideFromURL()

    const forced = getForcedMobile()
    const detected = isHandheldByHeuristics()
    const isHandheld = forced ?? detected

    window.__IS_HANDHELD__ = isHandheld
    // atributo na <html> ajuda CSS/JS a reagir sem importar nada
    if (isHandheld) document.documentElement.setAttribute('data-handheld', '1')
    else document.documentElement.removeAttribute('data-handheld')

    console.log(
        `[Movatic] modo ${isHandheld ? 'HANDHELD' : 'DESKTOP'}  (forced=${forced ?? 'auto'
        }, detected=${detected})`,
    )
}
setupHandheldFlag()

/* ------------------------------------------------------------------ */
/* 2) Tema + i18n                                                      */
/* ------------------------------------------------------------------ */

initTheme()

// Mantém o atributo lang do <html> sincronizado com o i18n
function applyHtmlLang(lng?: string) {
    const code = (lng || i18n.language || 'pt').slice(0, 2)
    document.documentElement.lang = code
}
applyHtmlLang()
i18n.on('languageChanged', applyHtmlLang)

/* ------------------------------------------------------------------ */
/* 3) Banner/atualização do PWA (compatível com qualquer versão do    */
/*    vite-plugin-pwa)                                                */
/* ------------------------------------------------------------------ */

// Textos do banner PWA por idioma (simples e embutido)
const PWA_TEXT = {
    pt: {
        update: 'Nova versão disponível',
        refresh: 'Atualizar',
        offlineReady: 'App pronto para uso offline',
    },
    en: { update: 'New version available', refresh: 'Update', offlineReady: 'App ready for offline use' },
    es: { update: 'Nueva versión disponible', refresh: 'Actualizar', offlineReady: 'App lista para uso sin conexión' },
    fr: { update: 'Nouvelle version disponible', refresh: 'Mettre à jour', offlineReady: 'Application prête pour une utilisation hors ligne' },
    it: { update: 'Nuova versione disponibile', refresh: 'Aggiorna', offlineReady: 'App pronta per l’uso offline' },
} as const
function tPwa() {
    const key = (i18n.language || 'pt').slice(0, 2) as keyof typeof PWA_TEXT
    return PWA_TEXT[key] ?? PWA_TEXT.pt
}

/** —— Type shim para compatibilizar qualquer versão do vite-plugin-pwa —— */
type MinimalRegisterSW = (opts?: {
    immediate?: boolean
    onNeedRefresh?: () => void
    onOfflineReady?: () => void
}) => (reloadPage?: boolean) => Promise<void>
const registerAny = registerSW as unknown as MinimalRegisterSW
/** ———————————————————————————————————————————————————————————————— */

// PWA updates (usando o shim acima)
const updateSW = registerAny({
    immediate: true,
    onNeedRefresh() {
        const { update, refresh } = tPwa()
        const container = document.createElement('div')
        container.setAttribute('data-pwa-banner', 'need-refresh')
        container.innerHTML = `
      <div style="position:fixed;bottom:16px;left:50%;transform:translateX(-50%);
                  padding:10px 14px;border-radius:12px;background:#111;color:#fff;
                  z-index:9999;box-shadow:0 10px 30px -15px rgba(0,0,0,.5)">
        ${update}
        <button id="upd" style="margin-left:8px;padding:6px 10px;border-radius:10px;
                                 background:#2563eb;color:#fff;border:0">${refresh}</button>
      </div>`
        document.body.appendChild(container)
        container
            .querySelector<HTMLButtonElement>('#upd')
            ?.addEventListener('click', () => {
                updateSW(true)
                container.remove()
            })
    },
    onOfflineReady() {
        const { offlineReady } = tPwa()
        const container = document.createElement('div')
        container.setAttribute('data-pwa-banner', 'offline-ready')
        container.innerHTML = `
      <div style="position:fixed;top:16px;right:16px;padding:8px 12px;border-radius:12px;
                  background:#16a34a;color:#fff;z-index:9999;box-shadow:0 10px 30px -15px rgba(0,0,0,.5)">
        ${offlineReady}
      </div>`
        document.body.appendChild(container)
        setTimeout(() => container.remove(), 2500)
    },
})

/* ------------------------------------------------------------------ */
/* 4) Mount da aplicação — APENAS UM RouterProvider                    */
/* ------------------------------------------------------------------ */

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <ToastProvider>
                <RouterProvider router={router} />
            </ToastProvider>
        </QueryClientProvider>
    </React.StrictMode>,
)
